import clsx from 'clsx';
import { CSSProperties, FunctionComponent, MouseEvent, SyntheticEvent, useContext } from 'react';
import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { Play, Volume2, VolumeX } from 'react-feather';

import { CropHint } from '@vestico/model';

import { useElementSize } from '../hooks';
import { useCropHintStyles } from '../hooks/useCropHintStyles';
import { Button } from './Button';
import { DragMove, Movement } from './DragMove';
import { MediaObjectFit } from './MediaDisplayConstants';
import Spinner, { SpinnerPositioning, SpinnerSize } from './Spinner';
import { VideoPlaybackContext } from './VideoPlaybackContextProvider';
import style from './VideoPlayer.module.css';

const PLAY_BUTTON_SIZE = 72;
const BUTTON_SIZE = 32;
const BUTTON_STROKE_WIDTH = 2;

export interface VideoPlayerProps {
  url: string;
  coverURL?: string;
  mimeType: string;
  onLoad?: () => void;
  onControlsHide?: (hide: boolean, videoControlProgressBarHeight: number) => void;
  videoFit?: MediaObjectFit;
  cropHint?: CropHint;
  disableControls?: boolean;
  showLoadingSpinner?: boolean;
}

export const VideoPlayer: FunctionComponent<VideoPlayerProps> = ({
  url,
  coverURL,
  mimeType,
  onLoad,
  onControlsHide,
  videoFit,
  cropHint,
  disableControls,
  showLoadingSpinner,
}) => {
  const rootRef = useRef<HTMLDivElement>(null);
  const videoRef = useRef<HTMLVideoElement>(null);
  const progressWrapperRef = useRef<HTMLDivElement>(null);

  const [playbackPercentage, setPlaybackPercentage] = useState(0);
  const [mainControlsButtonHidden, setMainControlsHidden] = useState(true);

  const [videoLoaded, setVideoLoaded] = useState(false);
  const [isPlaying, setPlaying] = useState(true);
  const [videoDidStartPlayback, setVideoDidStartPlayback] = useState(false);

  const [mediaAspectRatio, setMediaAspectRatio] = useState<number | undefined>(undefined);
  const containerDimensions = useElementSize(rootRef);
  const { position: cropHintPosition } = useCropHintStyles({ cropHint, mediaAspectRatio, containerDimensions });

  useLayoutEffect(() => {
    setPlaybackPercentage(0);
    setVideoLoaded(false);
    setVideoDidStartPlayback(false);
  }, [url]);

  const [videoCSSProperties, setVideoCSSProperties] = useState<CSSProperties>({});
  const rootDimensions = useElementSize(rootRef);

  const getVideoElement = useCallback(() => {
    if (!videoRef) {
      return undefined;
    }

    const { current } = videoRef;
    return current;
  }, [videoRef]);

  const updateMediaAspectRatio = useCallback(() => {
    const current = getVideoElement();
    if (!current) {
      return;
    }

    const { videoWidth, videoHeight } = current;
    if (!videoWidth || !videoHeight) {
      setMediaAspectRatio(undefined);
    } else {
      setMediaAspectRatio(videoWidth / videoHeight);
    }
  }, [getVideoElement]);

  const getProgressWrapperElement = useCallback(() => {
    if (!progressWrapperRef) {
      return undefined;
    }

    const { current } = progressWrapperRef;
    return current;
  }, [progressWrapperRef]);

  const adjustVideoCSSProperties = useCallback(() => {
    if (videoFit === 'contain') {
      return;
    }

    if (!mediaAspectRatio) {
      return;
    }

    if (!rootDimensions) {
      return;
    }

    const { width: elementWidth, height: elementHeight } = rootDimensions;

    const frameAspect = elementWidth / elementHeight;
    const adjustTop = mediaAspectRatio < frameAspect;

    const cssProperties: CSSProperties = {};
    if (adjustTop) {
      const height = (1 / (mediaAspectRatio / frameAspect)) * elementHeight;
      const top = (height - elementHeight) * -0.5;
      cssProperties.top = cropHintPosition?.y ?? top;
      cssProperties.height = height;
    } else {
      const width = (1 / (frameAspect / mediaAspectRatio)) * elementWidth;
      const left = (width - elementWidth) * -0.5;
      cssProperties.left = cropHintPosition?.x ?? left;
      cssProperties.width = width;
    }

    setVideoCSSProperties(cssProperties);
  }, [mediaAspectRatio, rootDimensions, videoFit, cropHintPosition]);

  useLayoutEffect(() => {
    adjustVideoCSSProperties();
  }, [adjustVideoCSSProperties]);

  const onVideoStartPlay = useCallback(() => {
    if (videoDidStartPlayback) {
      return;
    }

    setPlaying(false);
    const videoElement = getVideoElement();
    videoElement?.pause();
    setPlaying(true);
    setVideoDidStartPlayback(true);
    onLoad?.();
  }, [getVideoElement, onLoad, videoDidStartPlayback]);

  const onVideoLoad = useCallback(() => {
    if (videoLoaded) {
      return;
    }

    updateMediaAspectRatio();

    setVideoLoaded(true);
    onVideoStartPlay();

    // Keep observing URL too
  }, [videoLoaded, updateMediaAspectRatio, onVideoStartPlay]);

  // Update the progress bar
  const onTimeUpdate = useCallback(() => {
    const videoElement = getVideoElement();
    if (!videoElement || Number.isNaN(videoElement.duration)) {
      return;
    }

    // Work out how much of the media has played via the duration and currentTime parameters
    const percentage = (100 / videoElement.duration) * videoElement.currentTime;
    setPlaybackPercentage(percentage);
  }, [getVideoElement]);

  const blockEvent = useCallback((e: SyntheticEvent<HTMLElement>) => {
    e.preventDefault();
    e.stopPropagation();
  }, []);

  const updateChromeVisibility = useCallback(
    (hidden: boolean, e?: MouseEvent<HTMLElement>) => {
      if (disableControls) {
        return;
      }

      if (onControlsHide) {
        if (e) {
          blockEvent(e);
        }

        const progressWrapper = getProgressWrapperElement();
        onControlsHide(hidden, progressWrapper?.clientHeight ?? 0);
      }
      setMainControlsHidden(hidden);
      setPlaying(hidden);
    },
    [disableControls, onControlsHide, getProgressWrapperElement, blockEvent]
  );

  const handleMainViewClick = useCallback(
    (e?: MouseEvent<HTMLElement>) => {
      const newPlaybackButtonHidden = !mainControlsButtonHidden;
      updateChromeVisibility(newPlaybackButtonHidden, e);
    },
    [updateChromeVisibility, mainControlsButtonHidden]
  );

  useEffect(() => {
    const videoElement = getVideoElement();
    if (!videoElement) {
      return;
    }
    if (!videoDidStartPlayback) {
      return;
    }

    const videoisPlaying = !videoElement.paused;

    if (isPlaying && playbackPercentage > 99) {
      videoElement.currentTime = 0;
      if (videoisPlaying) {
        return;
      }
    }

    if (isPlaying && !videoisPlaying) {
      videoElement.play().catch((e) => {
        console.error('play error', e);
        updateChromeVisibility(false);
      });
    } else if (!isPlaying && videoisPlaying) {
      videoElement.pause();
    }
  }, [isPlaying, playbackPercentage, videoRef, videoLoaded, getVideoElement, videoDidStartPlayback, updateChromeVisibility]);

  const [initialDragPercentage, setInitialDragPercentage] = useState(0);

  const updateVideoProgress = useCallback(
    ({ x }: Movement) => {
      const video = getVideoElement();
      const progressWrapper = getProgressWrapperElement();
      if (!video || !progressWrapper) {
        return 0;
      }

      const percent = initialDragPercentage + x / progressWrapper.offsetWidth;
      video.currentTime = percent * video.duration;
      setPlaybackPercentage(percent * 100);
      return percent;
    },
    [getProgressWrapperElement, getVideoElement, initialDragPercentage]
  );

  const onProgressPointerDown = useCallback(
    (initialPosition: Movement) => {
      const video = getVideoElement();
      const progressWrapper = getProgressWrapperElement();
      if (!video || !progressWrapper) {
        return;
      }

      const progressWrapperLeft = progressWrapper.getBoundingClientRect().left;
      initialPosition.x -= progressWrapperLeft;
      const newPercent = updateVideoProgress(initialPosition);
      setInitialDragPercentage(newPercent);

      video.pause();
    },
    [getVideoElement, getProgressWrapperElement, updateVideoProgress]
  );

  const onProgressPointerUp = useCallback(() => {
    setInitialDragPercentage(0);

    const video = getVideoElement();
    if (!video) {
      return;
    }

    if (isPlaying) {
      video.play().catch((e) => console.error(e));
    }
  }, [getVideoElement, isPlaying]);

  const onProgressDragMove = useCallback((movement: Movement) => updateVideoProgress(movement), [updateVideoProgress]);

  const { settings, onUpdate: onVolumeOnUpdate } = useContext(VideoPlaybackContext);
  const [volumeOn, setVolumeOn] = useState(settings.volumeOn);

  const hasAudio = useMemo(() => {
    if (!videoLoaded) {
      return false;
    }

    const video = getVideoElement();
    if (!video) {
      return false;
    }

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const anyVideoElement = video as any;
    return anyVideoElement.mozHasAudio || Boolean(anyVideoElement.webkitAudioDecodedByteCount) || Boolean(anyVideoElement.audioTracks?.length);
  }, [getVideoElement, videoLoaded]);

  const handleVolumeClick = useCallback(() => {
    if (!hasAudio) {
      return;
    }

    setVolumeOn(!volumeOn);
    onVolumeOnUpdate(!volumeOn);
  }, [hasAudio, onVolumeOnUpdate, volumeOn]);

  const fitVideoInFrame = videoFit === 'contain';
  const sanitizedPlaybackPercentage = useMemo(() => Math.max(Math.min(playbackPercentage, 100), 0), [playbackPercentage]);

  const controlsHidden = useMemo(() => disableControls || mainControlsButtonHidden, [disableControls, mainControlsButtonHidden]);

  return (
    <div className={clsx(style.root)} onClick={handleMainViewClick} ref={rootRef} aria-hidden="true">
      <div className={clsx(style.coverMirrorImg, { [style.hidden]: !fitVideoInFrame })}>
        {/* // skipcq JS-W1015 */}
        <img src={coverURL} className={clsx(style.content)} draggable={false} alt="cover" />
      </div>
      {/* skipcq JS-0754 */}
      <video
        className={clsx(style.content, style.video, { [style.fitInFrame]: fitVideoInFrame }, { [style.visible]: videoLoaded })}
        autoPlay
        playsInline
        draggable={false}
        poster={coverURL}
        src={url}
        onLoadedData={onVideoLoad}
        onTimeUpdate={onTimeUpdate}
        onPlay={onVideoStartPlay}
        ref={videoRef}
        muted={!volumeOn}
        style={videoCSSProperties}
      >
        <source type={mimeType} src={url} />
      </video>
      <div className={clsx(style.controls)}>
        <div className={clsx(style.control, style.volume, { [style.hidden]: disableControls })}>
          <Button
            content={
              !volumeOn || !hasAudio ? (
                <VolumeX size={BUTTON_SIZE} strokeWidth={BUTTON_STROKE_WIDTH} />
              ) : (
                <Volume2 size={BUTTON_SIZE} strokeWidth={BUTTON_STROKE_WIDTH} />
              )
            }
            plain
            onClick={handleVolumeClick}
            ariaLabel={!volumeOn || !hasAudio ? 'Enable audio' : 'Disable audio'}
          />
        </div>
        <div className={clsx(style.control, style.play, { [style.hidden]: controlsHidden })}>
          <Play size={PLAY_BUTTON_SIZE} strokeWidth={BUTTON_STROKE_WIDTH} />
        </div>
        <DragMove onDragMove={onProgressDragMove} onPointerDown={onProgressPointerDown} onPointerUp={onProgressPointerUp} draggingEnabled blockEvents>
          <div
            className={clsx(style.control, style.progressWrapper, { [style.hidden]: controlsHidden })}
            onMouseMove={blockEvent}
            onTouchMove={blockEvent}
            ref={progressWrapperRef}
            role="progressbar"
            tabIndex={-1}
          >
            <progress className={clsx(style.progress)} max="100" value={sanitizedPlaybackPercentage}>
              {`${sanitizedPlaybackPercentage}% played`}
            </progress>
          </div>
        </DragMove>
      </div>
      {showLoadingSpinner && !videoLoaded ? <Spinner size={SpinnerSize.medium} positioning={SpinnerPositioning.transform} /> : null}
    </div>
  );
};
